import {
  Breadcrumbs,
  ConfirmModal,
  FlexElement,
  routable,
  useModalContext,
  useNotificationContext,
} from '@hulanbv/dashboard-components';
import { Role } from '@hulanbv/ramvrie-packages/enums';
import { ICard } from '@hulanbv/ramvrie-packages/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { hasRole } from '../../domain/authentication/utilities/has-role.utility';
import { cardService } from '../../domain/cards/card.service';
import { routes } from '../../domain/common/constants/routes.constant';
import { CardForm } from '../templates/forms/card-form.template';
import { CardsScreen } from './cards.screen';
import { DecksScreen } from './decks.screen';
import { NotFoundScreen } from './not-found.screen';

export const CardUpdateScreen = routable(
  {
    root: {
      path: (deckId = ':deckId', cardId = ':cardId') =>
        `/decks/${deckId}/cards/${cardId}/update`,
      isAccessible: () => hasRole(Role.ADMIN, Role.MANAGER),
      name: () => 'Update card',
    },
  },
  () => {
    const { openModal } = useModalContext();
    const { addNotification } = useNotificationContext();
    const navigate = useNavigate();

    const [card, setCard] = useState<ICard>();

    const { deckId, cardId } = useParams<'deckId' | 'cardId'>();

    if (!deckId || !cardId) {
      return <Navigate to={NotFoundScreen.paths.root()} replace />;
    }

    useEffect(() => {
      cardService.get(cardId).then((response) => setCard(response.data));
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount
    }, []);

    const submit = useCallback(
      async (formData: FormData) => {
        try {
          await cardService.put(formData);
          navigate(CardsScreen.paths.root(deckId), {
            replace: true,
          });

          addNotification(() => 'Successfully created the deck', 'success');
        } catch {
          addNotification(
            () => 'Something went wrong while creating the deck',
            'danger',
          );
        }
      },
      [addNotification, navigate, deckId],
    );

    const deleteModel = useCallback(async () => {
      const isConfirmed = await openModal<boolean>((resolve) => (
        <ConfirmModal
          resolve={resolve}
          title="Are you sure you want to delete this deck?"
          flavor="danger"
        />
      ));
      if (!isConfirmed) {
        return;
      }

      try {
        await cardService.delete(cardId);
        navigate(DecksScreen.paths.root());
        addNotification(() => 'Successfully deleted the card', 'success');
      } catch {
        addNotification(
          () => 'Something went wrong while deleting the card',
          'danger',
        );
      }
    }, [openModal, addNotification, cardId, navigate]);

    return (
      <FlexElement alignItems="stretch">
        <Breadcrumbs routes={routes} />
        {card && (
          <CardForm
            model={card}
            onDelete={deleteModel}
            deckId={deckId}
            onSubmit={submit}
          />
        )}
      </FlexElement>
    );
  },
);
